// src/components/ImageCarousel.tsx
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext'; // Add this import

// Import your images
import image2 from '../assets/gallery/carousel/image2.png';
import image4 from '../assets/gallery/carousel/image4.png';
import image5 from '../assets/gallery/carousel/image5.jpg';
import wormImage from '../assets/gallery/image/worminhand.jpeg';

const ImageCarousel: React.FC = () => {
  const { t } = useLanguage(); // Add this line
  
  const images = [
    { id: 2, src: image2, alt: 'Gallery image 2' },
    { id: 5, src: image5, alt: 'Gallery image 5' },
    { id: 4, src: image4, alt: 'Gallery image 4' },
    { id: 10, src: wormImage, alt: 'Worm in hand' },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    const interval = setInterval(goToNext, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-16 bg-white dark:bg-emerald-950/20">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-4xl font-extrabold text-center mb-4 text-emerald-900 dark:text-white">
          {t('gallery.title', 'Our Gallery')}
        </h2>
        <p className="text-center text-emerald-800/60 dark:text-emerald-100/40 mb-12 max-w-2xl mx-auto">
          {t('gallery.subtitle', 'Take a look at our vermicomposting process and quality products')}
        </p>
        
        {/* Rest of your component remains the same */}
        <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-emerald-50 dark:bg-emerald-950/40">
          {/* Main image container */}
          <div className="relative flex items-center justify-center min-h-[400px] md:min-h-[500px] lg:min-h-[600px] p-4">
            <img
              src={images[currentIndex].src}
              alt={images[currentIndex].alt}
              className="max-w-full max-h-[400px] md:max-h-[500px] lg:max-h-[600px] w-auto h-auto object-contain rounded-lg"
            />
            
            {/* Image counter */}
            <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              {currentIndex + 1} / {images.length}
            </div>
          </div>

          {/* Navigation arrows */}
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-emerald-900 p-3 rounded-full shadow-lg transition-all hover:scale-110"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-emerald-900 p-3 rounded-full shadow-lg transition-all hover:scale-110"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Thumbnail navigation */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 px-4 py-2 bg-black/30 rounded-full backdrop-blur-sm">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-primary w-6' 
                    : 'bg-white/60 hover:bg-white'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImageCarousel;